import type { JSX, ReactNode } from 'react';
import { Info, Lightbulb, TriangleAlert, CircleCheck, type LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/cn.js';

export type CalloutType = 'note' | 'tip' | 'warning' | 'success';

const STYLES: Record<
  CalloutType,
  { accent: string; border: string; label: string; Icon: LucideIcon }
> = {
  note: { accent: 'text-accent-blue', border: 'border-accent-blue/30', label: 'Note', Icon: Info },
  tip: {
    accent: 'text-accent-cyan',
    border: 'border-accent-cyan/30',
    label: 'Tip',
    Icon: Lightbulb,
  },
  warning: {
    accent: 'text-accent-rose',
    border: 'border-accent-rose/30',
    label: 'Heads up',
    Icon: TriangleAlert,
  },
  success: {
    accent: 'text-accent-lime',
    border: 'border-accent-lime/30',
    label: 'Good to know',
    Icon: CircleCheck,
  },
};

export interface CalloutProps {
  type?: CalloutType;
  title?: string;
  children?: ReactNode;
}

export function Callout({ type = 'note', title, children }: CalloutProps): JSX.Element {
  // eslint-disable-next-line security/detect-object-injection -- type is a fixed CalloutType union, not user input
  const style = STYLES[type];
  const { Icon } = style;
  return (
    <div
      className={cn('my-5 rounded-md border border-l-2 bg-white/[0.02] px-4 py-3.5', style.border)}
    >
      <div
        className={cn(
          'mb-1.5 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.1em]',
          style.accent,
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        {title ?? style.label}
      </div>
      <div className="text-[14px] leading-[1.6] text-secondary [&>p:not(:first-child)]:mt-2 [&>p]:m-0">
        {children}
      </div>
    </div>
  );
}
